import React, { useEffect, useState, useCallback } from 'react';
import { Spin, Alert, Card, Button, Tabs } from 'antd';
import Layout from '../../components/Layout';
import AntTable from '../../components/CustomTable/AntTable';
import { customBaseUrl, httpGet } from '../../store/http';
import { NotificationManager } from 'react-notifications';
import CreateCommissionModal from './CreateCommissionModal';
import AllReferrals from '../Referrals/AllReferrals';
import AllReferralCustomers from '../Referrals/AllReferralCustomers';

const { TabPane } = Tabs;

const Commission = () => {
    const [commissions, setCommissions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);

    const fetchCommissions = useCallback(async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const res = await httpGet(`${customBaseUrl.referralUrl}/api/v1/admin/fetchAllCommissions?pageNo=${page - 1}&pageSize=${pageSize}`);
            if (res?.data?.content) {
                setCommissions(res.data.content);
                setPagination({
                    current: (res.data.number ?? 0) + 1,
                    pageSize: res.data.size ?? pageSize,
                    total: res.data.totalElements ?? 0,
                });
            } else {
                setError(res?.message || 'Failed to fetch commissions.');
            }
        } catch (err) {
            setError('An error occurred while fetching commissions.');
            NotificationManager.error('An error occurred while fetching commissions.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCommissions(pagination.current, pagination.pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchCommissions]);

    const handleTableChange = (pagination) => {
        fetchCommissions(pagination.current, pagination.pageSize);
    };

    const showCreateModal = () => {
        setIsCreateModalVisible(true);
    };

    const handleCreateModalCancel = () => {
        setIsCreateModalVisible(false);
    };

    const handleCreateCommission = () => {
        // Logic to handle commission creation
        // After successful creation, refresh the list
        fetchCommissions(pagination.current, pagination.pageSize);
        setIsCreateModalVisible(false);
    };

    const columns = [
        { title: 'Name', dataIndex: 'name', key: 'name' },
        { title: 'Transaction Type', dataIndex: 'transactionType', key: 'transactionType' },
        { title: 'Amount Type', dataIndex: 'amountType', key: 'amountType' },
        { title: 'Amount', dataIndex: 'amount', key: 'amount' },
        { title: 'Percentage Customer', dataIndex: 'percentageCustomer', key: 'percentageCustomer' },
        { title: 'Who To Credit', dataIndex: 'whoToCredit', key: 'whoToCredit' },
        { title: 'Customer Type', dataIndex: 'customerType', key: 'customerType' },
        { title: 'Recurrent', dataIndex: 'recurrent', key: 'recurrent', render: (text) => (text ? 'Yes' : 'No') },
        { title: 'Created At', dataIndex: 'createdAt', key: 'createdAt' },
        { title: 'Modified At', dataIndex: 'modifiedAt', key: 'modifiedAt' },
    ];

    return (
        <Layout>
            <Card title="Commission Management">
                <Tabs defaultActiveKey="1">
                    <TabPane tab="Commissions" key="1">
                        <Button type="primary" onClick={showCreateModal} style={{ marginBottom: '20px' }}>
                            Create Commission
                        </Button>
                        {loading ? (
                            <Spin tip="Loading commissions..." />
                        ) : error ? (
                            <Alert message="Error" description={error} type="error" showIcon />
                        ) : (
                            <AntTable
                                pagination={pagination}
                                users={commissions}
                                loading={loading}
                                handleGetNext={handleTableChange}
                                columns={columns}
                            />
                        )}
                    </TabPane>
                    <TabPane tab="All Referrals" key="2">
                        <AllReferrals />
                    </TabPane>
                    <TabPane tab="All Referral Customers" key="3">
                        <AllReferralCustomers />
                    </TabPane>
                </Tabs>
            </Card>
            <CreateCommissionModal
                visible={isCreateModalVisible}
                onCancel={handleCreateModalCancel}
                onCreate={handleCreateCommission}
            />
        </Layout>
    );
};

export default Commission;